import React from 'react';
import { getCPUAvatar } from '../utils/avatarHelpers';
import { formatScore } from '../utils/cardHelpers';

function ScoreBoard({ players, playerId, final = false }) {
  const sortedPlayers = [...(players || [])].sort((a, b) => {
    // For chaos/choice modes, use completedPhases length to determine progress. For normal mode, use currentPhase.
    const aProgress = a.completedPhases ? a.completedPhases.length : a.currentPhase;
    const bProgress = b.completedPhases ? b.completedPhases.length : b.currentPhase;
    
    if (bProgress !== aProgress) return bProgress - aProgress;
    return a.score - b.score;
  });

  const rankMedals = ['🥇', '🥈', '🥉'];


  return (
    <div style={{
      background: 'linear-gradient(160deg, #0d2918, #0d1f14)',
      border: '1px solid rgba(212,168,67,0.3)',
      borderRadius: '10px',
      padding: '24px',
      minWidth: '300px',
      boxShadow: '0 0 0 1px rgba(13,31,20,0.9), 0 10px 40px rgba(0,0,0,0.6)',
    }}>
      {/* Title */}
      <div className="text-center mb-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.5))' }} />
          <span style={{ color: 'rgba(212,168,67,0.5)', fontSize: '0.55rem' }}>◆◆◆</span>
          <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(212,168,67,0.5), transparent)' }} />
        </div>
        <h2 style={{
          fontFamily: '"Cinzel", serif',
          fontSize: '1.1rem',
          fontWeight: '700',
          color: '#d4a843',
          letterSpacing: '0.15em',
          textShadow: '0 0 15px rgba(212,168,67,0.4)',
        }}>
          {final ? 'FINAL SCORES' : 'SCOREBOARD'}
        </h2>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(212,168,67,0.2)', paddingBottom: '8px' }}>
            {['#', 'Player', 'Phase', 'Score'].map((h, i) => (
              <th key={h} style={{
                padding: '0 4px 10px',
                fontFamily: '"Cinzel", serif',
                fontSize: '0.65rem',
                letterSpacing: '0.12em',
                color: 'rgba(212,168,67,0.55)',
                fontWeight: '400',
                textAlign: i === 0 ? 'left' : i === 2 ? 'center' : i === 3 ? 'right' : 'left',
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedPlayers.map((player, index) => {
            const isYou    = player.id === playerId;
            const isWinner = final && index === 0;

            return (
              <tr
                key={player.id}
                style={{
                  borderBottom: '1px solid rgba(212,168,67,0.08)',
                  background: isYou ? 'rgba(212,168,67,0.06)' : 'transparent',
                  transition: 'background 0.2s ease',
                }}
              >
                {/* Rank */}
                <td style={{ padding: '12px 4px' }}>
                  {index < 3 ? (
                    <span style={{ fontSize: '1.1rem' }}>{rankMedals[index]}</span>
                  ) : (
                    <span style={{
                      width: '22px', height: '22px',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: '50%',
                      background: 'rgba(245,234,210,0.08)',
                      color: 'rgba(245,234,210,0.5)',
                      fontSize: '0.75rem',
                      fontFamily: '"Cinzel", serif',
                    }}>
                      {index + 1}
                    </span>
                  )}
                </td>

                {/* Player name */}
                <td style={{ padding: '12px 4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontFamily: '"Philosopher", "Noto Serif SC", Georgia, serif',
                      fontWeight: '600',
                      color: isWinner ? '#d4a843' : '#f5ead2',
                      fontSize: '0.9rem',
                    }}>
                      {player.name}
                    </span>
                    {isYou && (
                      <span style={{ color: '#d4a843', fontSize: '0.65rem', fontFamily: '"Cinzel",serif' }}>(You)</span>
                    )}
                    {player.isComputer && (
                      <span style={{ fontSize: '0.85rem', marginLeft: '4px' }} title="CPU Player">
                        {getCPUAvatar(player.id || player.name)}
                      </span>
                    )}
                    {isWinner && <span>👑</span>}
                  </div>
                </td>

                {/* Phase */}
                <td style={{ padding: '12px 4px', textAlign: 'center' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '52px', position: 'relative' }}>
                    <span className="phase-badge">{player.currentPhase}</span>
                    {player.completedPhaseThisRound && (
                      <span style={{ position: 'absolute', right: '0', color: '#27ae60', fontSize: '0.9rem', fontWeight: 'bold' }}>✓</span>
                    )}
                  </div>
                </td>

                {/* Score */}
                <td style={{ padding: '12px 4px', textAlign: 'right' }}>
                  <span style={{
                    fontFamily: '"Cinzel", serif',
                    fontWeight: '700',
                    fontSize: '0.95rem',
                    color: isWinner ? '#d4a843' : '#f5ead2',
                  }}>
                    {formatScore(player.score)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Legend */}
      <div style={{
        marginTop: '16px',
        paddingTop: '12px',
        borderTop: '1px solid rgba(212,168,67,0.1)',
        fontSize: '0.65rem',
        color: 'rgba(245,234,210,0.35)',
        fontFamily: '"IM Fell English SC", Georgia, serif',
        letterSpacing: '0.03em',
      }}>
        <p style={{ fontFamily: '"IM Fell English SC", Georgia, serif' }}>Lower score is better · 1–9 = 5pts · Wild/Skip = 25pts</p>
      </div>
    </div>
  );
}

/* ============================================================
   MINI SCORE BOARD (in-game overlay)
   ============================================================ */
export function MiniScoreBoard({ players, playerId, currentPlayerId }) {
  return (
    <div className="flex flex-wrap gap-2">
      {players?.map(player => (
        <div
          key={player.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '5px',
            border: player.id === currentPlayerId
              ? '1px solid rgba(212,168,67,0.7)'
              : player.id === playerId
                ? '1px solid rgba(212,168,67,0.35)'
                : '1px solid rgba(245,234,210,0.08)',
            background: player.id === currentPlayerId
              ? 'rgba(212,168,67,0.15)'
              : player.id === playerId
                ? 'rgba(212,168,67,0.06)'
                : 'rgba(13,31,20,0.5)',
            boxShadow: player.id === currentPlayerId
              ? '0 0 12px rgba(212,168,67,0.3)'
              : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: player.id === currentPlayerId ? '#d4a843' : '#f5ead2' }}>
            {player.name}
            {player.id === playerId && ' (You)'}
          </span>
          <span className="phase-badge" style={{ fontSize: '0.65rem' }}>P{player.currentPhase}</span>
          <span className="score-badge" style={{ fontSize: '0.65rem' }}>{player.score}</span>
        </div>
      ))}
    </div>
  );
}

export default ScoreBoard;
